import { DeviceUsageSvg } from './DeviceUsageSvg';
import { Box, Button, Bookmark, Text, Close, Pressable } from '../../../../../../mads';
import { logAppPromoInteraction } from '../../../../../../shared/app-promo/logAppPromoInteraction';
import { getOnelinkUrlInner } from '../../../../../../shared/app-promo/oneLink';
import { useI18n } from '../../../../../../shared/I18n';
import {
    useViewJobDataContext,
    useViewjobOneGraphModel,
} from '../../../../context/ViewJobDataContext';
import { ViewJobDivider } from '../../Divider';

import { spacing } from '@indeed/ifl-tokens/react-native';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal, StyleSheet } from 'react-native';

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: spacing.s4,
        paddingVertical: spacing.s2,
        rowGap: spacing.s2,
    },
    topDivider: {
        marginTop: spacing.s5,
        marginBottom: spacing.s5,
    },
    topSpacer: {
        marginTop: spacing.s5,
    },
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        justifyContent: 'flex-end',
        width: '100%',
        height: '100%',
        zIndex: 9050,
        bottom: 0,
        left: 0,
    },
    modalContent: {
        backgroundColor: '#ffffff',
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingTop: 56,
        paddingHorizontal: 16,
        paddingBottom: 24,
        maxHeight: '90%',
        alignItems: 'center',
    },
    closeButton: {
        position: 'absolute',
        top: 16,
        right: 16,
        width: 44,
        height: 44,
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
    },
    title: {
        fontSize: 24,
        fontWeight: '700',
        textAlign: 'center',
        color: '#2d2d2d',
        marginBottom: 8,
    },
    subtitle: {
        fontSize: 16,
        textAlign: 'center',
        color: '#2d2d2d',
    },
});

interface SaveJobButtonProps {
    topSpacerMode?: 'none' | 'divider' | 'spacer';
}

export function BottomSaveJobButton({ topSpacerMode = 'none' }: SaveJobButtonProps) {
    const { gettext } = useI18n();
    const [modalVisible, setModalVisible] = useState(false);
    const viewjobOneGraphModel = useViewjobOneGraphModel();
    const viewJobDataContext = useViewJobDataContext();
    const hasLoggedImpression = useRef(false);

    const ctk = viewjobOneGraphModel?.ctk;
    const mobtk = viewjobOneGraphModel?.mobvjtk;
    const isLoggedIn = viewjobOneGraphModel?.jobSeeker?.loggedIn;
    const country = viewJobDataContext.initialData?.country;
    const language = viewJobDataContext.initialData?.language;

    const handleLogInteraction = useCallback(
        (interactionType: 'IMPRESSION' | 'CLICK' | 'DISMISS') => {
            logAppPromoInteraction(
                { ctk, mobtk, isLoggedIn, country, language },
                {
                    hostId: 'jobsearch',
                    pageId: 'viewjob',
                    campaign: 'jsj_rn_web_viewjob',
                    experiments: [],
                },
                interactionType,
            );
        },
        [ctk, mobtk, isLoggedIn, country, language],
    );

    useEffect(() => {
        if (modalVisible && !hasLoggedImpression.current) {
            hasLoggedImpression.current = true;
            handleLogInteraction('IMPRESSION');
        }
    }, [modalVisible, handleLogInteraction]);

    const handleOpenModal = useCallback(() => {
        setModalVisible(true);
    }, []);

    const handleCloseModal = useCallback(() => {
        handleLogInteraction('DISMISS');
        setModalVisible(false);
        hasLoggedImpression.current = false;
    }, [handleLogInteraction]);

    const handleContinueClick = useCallback(() => {
        handleLogInteraction('CLICK');
    }, [handleLogInteraction]);

    // We are only running test for US at the moment
    // See https://indeed.sourcegraph.com/code.corp.indeed.com/mag-user-acquisition-devs/mosaic-provider-app-download-promos/-/blob/src/components/SaveJobPromo.tsx?L109
    // when expanding test
    const title =
        country === 'US' ? gettext('Want to apply later?') : gettext('Save jobs wherever you are');

    const subtitle = country === 'US' ? gettext('Use your Indeed app to save this job') : undefined;

    const oneLinkUrl = getOnelinkUrlInner('rn_vj-save_job', [], isLoggedIn || false);

    return (
        <>
            {topSpacerMode === 'divider' && <ViewJobDivider sx={styles.topDivider} />}
            <Box sx={[styles.container, topSpacerMode === 'spacer' && styles.topSpacer]}>
                <Button
                    onPress={handleOpenModal}
                    variant="utility"
                    size="lg"
                    iconBefore={<Bookmark variant="stroke" color="rgb(45, 45, 45)" />}
                >
                    {gettext('Save job')}
                </Button>
            </Box>

            <Modal
                visible={modalVisible}
                transparent={true}
                animationType="none"
                onRequestClose={handleCloseModal}
            >
                <Pressable
                    sx={styles.modalOverlay}
                    onPress={handleCloseModal}
                    accessibilityRole="none"
                    accessibilityLabel={gettext('Close modal')}
                >
                    <Box sx={styles.modalContent}>
                        <Pressable
                            sx={styles.closeButton}
                            onPress={handleCloseModal}
                            accessibilityLabel={gettext('Close')}
                            accessibilityRole="button"
                        >
                            <Close color="#767676" />
                        </Pressable>
                        <Text level={4} sx={styles.title}>
                            {title}
                        </Text>

                        {subtitle && (
                            <Text level={3} sx={styles.subtitle}>
                                {subtitle}
                            </Text>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: '100%',
                                marginTop: 16,
                                marginBottom: 24,
                            }}
                        >
                            <DeviceUsageSvg />
                        </Box>
                        <Box sx={{ width: '80%', marginStart: spacing.s2, marginEnd: spacing.s2 }}>
                            <Button
                                variant={'primary'}
                                size="lg"
                                onPress={handleContinueClick}
                                href={oneLinkUrl}
                                accessibilityRole="button"
                                ariaLabel={gettext('Continue on the app')}
                            >
                                <Text sx={{ color: 'white' }}>
                                    {gettext('Continue on the app')}
                                </Text>
                            </Button>
                        </Box>
                    </Box>
                </Pressable>
            </Modal>
        </>
    );
}
